"use client";

import { useEffect, useState } from "react";
import { Calendar, ShoppingBag } from "lucide-react";

import { RomaOrderCta } from "@/components/roma/roma-order-chooser";
import { useRomaReserveSheet } from "@/components/roma/roma-reserve-sheet-context";
import type { RomaReserveLocation } from "@/components/roma/roma-types";

type RomaDarkFunnelMobileBarProps = {
  locations: readonly RomaReserveLocation[];
  showOrder?: boolean;
};

const MOBILE_STICKY_LOCATION = "home_dark_funnel_mobile_sticky";

/**
 * Fixed bottom quick-actions bar, same two-button card format as Tuscan
 * Cove's homepage bar (Order first, Reserve beside it), recolored for the
 * dark funnel. Generic Order opens the location chooser.
 */
export function RomaDarkFunnelMobileBar({
  locations,
  showOrder = true,
}: RomaDarkFunnelMobileBarProps) {
  const reserveSheet = useRomaReserveSheet();
  const [footerInView, setFooterInView] = useState(false);

  useEffect(() => {
    const footer = document.querySelector("footer");
    let frame = 0;

    const updateVisibility = () => {
      const footerRect = footer?.getBoundingClientRect();

      setFooterInView(
        footerRect
          ? footerRect.top < window.innerHeight && footerRect.bottom > 0
          : false,
      );
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateVisibility();
      });
    };

    updateVisibility();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, []);

  const visible = !footerInView;

  if (locations.length === 0) {
    return null;
  }

  const orderClassName =
    "roma-funnel-btn-primary flex h-14 min-w-0 w-full items-center justify-center gap-2 rounded-xl bg-[#b91c1c] text-[15px] font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-[#cf1f1f] active:scale-[0.97]";

  return (
    <>
      <nav
        aria-label="Roma quick actions"
        aria-hidden={!visible}
        inert={!visible}
        data-funnel-priority="mobile-sticky-actions"
        className={`fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(0.5rem_+_env(safe-area-inset-bottom))] transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none md:hidden ${
          visible
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-full opacity-0"
        }`}
      >
        <div
          className={`mx-auto max-w-sm gap-2 rounded-2xl border border-white/10 bg-[#161616]/95 p-2 shadow-[0_18px_44px_-22px_rgba(0,0,0,0.85)] backdrop-blur ${
            showOrder ? "grid grid-cols-2" : "grid grid-cols-1"
          }`}
        >
          {showOrder ? (
            <RomaOrderCta
              trackingLocation={MOBILE_STICKY_LOCATION}
              className={orderClassName}
            >
              <ShoppingBag className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="truncate">Order</span>
            </RomaOrderCta>
          ) : null}
          <button
            type="button"
            className={`flex h-14 min-w-0 items-center justify-center gap-2 rounded-xl text-[15px] font-semibold transition-[background-color,transform] duration-200 active:scale-[0.97] ${
              showOrder
                ? "bg-white/10 text-white hover:bg-white/16"
                : "roma-funnel-btn-primary bg-[#b91c1c] text-white hover:bg-[#cf1f1f]"
            }`}
            onClick={() => reserveSheet?.openSheet()}
            data-track="reservation_clicked"
            data-track-location={MOBILE_STICKY_LOCATION}
          >
            <Calendar className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="truncate">Reserve</span>
          </button>
        </div>
      </nav>
    </>
  );
}
